// angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// addons
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

// service
import { AuthService, UserService } from '@core/services';

// store
import * as actions from './actions';
import * as uiActions from '../ui/actions';

// model
import {
  ILoginResponse,
  IUser,
  IUserLogin,
  IVerifyRequest,
  IVerifyResponse,
  LoginResult,
  OrganizationStatusEnum,
  ToastContextEnum,
  VerifyResult
} from '@shared/models';

@Injectable()
export class UserEffects {
  getCache$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.GetCache),
    mergeMap(() => {
      return this.authService.chechUserCache().pipe(
        map((c: any) => {
          return actions.getCacheSuccess({ user: {
              initials: c['initials'],
              firstName: c['firstName'],
              lastName: c['lastName'],
              email: c['email'],
              blocks: c['blocks'],
              certificates: c['certificates'],
              value: c['value'],
              paid: c['paid'],
              certificatesBalance: c['certificatesBalance'],
              organizationDateOfJoining: c['organizationDateOfJoining'],
              accessToken: c['accessToken'],
              expires: c['expires'],
              issued: c['issued'],
              role: c['role'],
              organizationName: c['organizationName'],
              organizationStatus: c['organizationStatus'],
              organization: {
                name: c['organization.name'],
                address: c['organization.address'],
                city: c['organization.city'],
                region: c['organization.region'],
                postalCode: c['organization.postalCode'],
                coc: c['organization.coc'],
                iban: c['organization.iban'],
                country: c['organization.country'],
                mobilePhone: c['organization.mobilePhone'],
                annualStaffTurnover: c['organization.annualStaffTurnover'],
                initialNumberOfCertificates: c['organization.initialNumberOfCertificates'],
                initialPercentageOfShares: c['organization.initialPercentageOfShares'],
                certificatesBalance: c['organization.certificatesBalance'],
                status: c['organization.status'],
                investor: null
              }
            } });
        }),
        catchError(_ => of(actions.logout()))
      );
    })
  ));

  login$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Login),
    mergeMap(({ loginCredentials }: { loginCredentials: IUserLogin }) => {
      return this.authService.login(loginCredentials).pipe(
        map((result: ILoginResponse) => {
          if (result.result === LoginResult.SUCCESS) {
            return actions.loginSuccess({user: result.token});
          } else {
            return actions.loginResponse({response: result});
          }
        }),
        catchError(error => of(actions.loginFail({ error })))
      );
    })
  ));

  loginResponse$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoginResponse),
    map(({ response }: { response: ILoginResponse }) => {
      switch (response.result) {
        case LoginResult.MFA_REQUIRED:
          return actions.emptyAction({});
        case LoginResult.MOBILE_PHONE_NOT_FOUND:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Your account has no mobile number.',
              context: ToastContextEnum.Error
            }});
        case LoginResult.SMS_SEND_FAILED:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Failed to send authentication SMS. Try again later.',
              context: ToastContextEnum.Error
            }});
        case LoginResult.FAIL:
        default:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Login failed. Check your credentials.',
              context: ToastContextEnum.Error
            }});
      }
    })
  ));

  loginSuccess$ = createEffect(() => this.actions$.pipe(
    ofType( actions.ActionTypes.LoginSuccess),
    tap(({ user: { organizationStatus, role }}: { user: IUser }) => {
      if (organizationStatus === OrganizationStatusEnum.NotInitialized) {
        this.router.navigate(['/initiate']);

        return;
      }

      this.router.navigate([role]);
    })
  ), { dispatch: false });

  verifyMfa$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.VerifyMfa),
    mergeMap(({ verifyRequest }: { verifyRequest: IVerifyRequest }) => {
      return this.authService.verifyMfa(verifyRequest).pipe(
        map((result: IVerifyResponse) => {
          if (result.result === VerifyResult.SUCCESS) {
            return actions.loginSuccess({user: result.token});
          } else {
            return actions.verifyResponse({response: result});
          }
        }),
        catchError(error => of(actions.loginFail({ error })))
      );
    })
  ));

  verifyResponse$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.VerifyResponse),
    map(({ response }: { response: IVerifyResponse }) => {
      switch (response.result) {
        case VerifyResult.WRONG_CODE:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Wrong authentication code.',
              context: ToastContextEnum.Error
            }});
        case VerifyResult.MFA_EXPIRED:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Code validity is expired to log in again.',
              context: ToastContextEnum.Error
            }});
        case VerifyResult.FAILED:
        default:
          return uiActions.openToast({toast: {
              id: Math.random(),
              message: 'Failed to verify authentication code.',
              context: ToastContextEnum.Error
            }});
      }
    })
  ));

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Logout),
    mergeMap(() => {
      return this.authService.logout().pipe(
        tap(() => this.router.navigate(['/login']))
      );
    })
  ), { dispatch: false });

  loadBalance$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoadBalance),
    mergeMap(() => {
      return this.userService.getUserBalance().pipe(
        map(userBalance => actions.loadBalanceSuccess({ userBalance }))
      );
    })
  ));

  navigateAfterInit$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.UpdateOrganization),
    tap(_ => this.router.navigate(['/']))
  ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private authService: AuthService,
    private router: Router
  ) {}
}
